import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { BilingualMarkdownEditor } from '../../components/MarkdownField'

/**
 * Bundle editor (EPIC #116 P1). Scoped to a bundle grouping — the grouping
 * comes from the URL (?groupingId= on create, the bundle's grouping on edit),
 * not a dropdown. Set the course, rounds, price, time-of-day window,
 * days-of-week, expiry, draft/active. Economics disabled for a managed child.
 */
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export default function BundleEdit() {
  const { t } = useLanguage()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isNew = !id

  const [groupingId, setGroupingId] = useState(searchParams.get('groupingId') || '')
  const [courses, setCourses] = useState([])
  // EPIC #116 P2 — a bundle can span several courses sharing one round pool,
  // each with an optional per-course minimum. [{ coursesId, minRounds }]
  const [selectedCourses, setSelectedCourses] = useState([])
  const [canEditPricing, setCanEditPricing] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [savedFlash, setSavedFlash] = useState(false)

  const [f, setF] = useState({
    name: '', description: '', descriptionFr: '', roundsCount: 4, price: '',
    timeWindowStart: '', timeWindowEnd: '', days: '1111111', expiryDate: '', status: 'draft',
  })
  const set = (k, v) => setF(s => ({ ...s, [k]: v }))

  useEffect(() => {
    (async () => {
      try {
        const [me, b] = await Promise.all([
          tournamentApi.get('/portal/reseller/me').catch(() => ({ data: {} })),
          id ? tournamentApi.get(`/portal/reseller/bundles/${id}`) : Promise.resolve(null),
        ])
        setCanEditPricing(me.data?.canEditPricing !== false)
        // Resolve the grouping: ?groupingId= on create, the bundle on edit.
        let gid = searchParams.get('groupingId')
        if (b) {
          const bn = b.data.bundle
          gid = bn.Grouping_ID
          setGroupingId(gid)
          setF({
            name: bn.Name || '', description: bn.Description || '', descriptionFr: bn.DescriptionFr || '',
            roundsCount: bn.RoundsCount, price: bn.Price ?? '',
            timeWindowStart: (bn.TimeWindowStart || '').slice(0, 5),
            timeWindowEnd: (bn.TimeWindowEnd || '').slice(0, 5),
            days: bn.DaysAllowed || '1111111',
            expiryDate: bn.ExpiryDate ? String(bn.ExpiryDate).slice(0, 10) : '',
            status: bn.Status || 'draft',
          })
          setSelectedCourses((b.data.courses || []).map(c => ({
            coursesId: c.Courses_ID, minRounds: Number(c.MinRounds) || 0,
          })))
        }
        // Only the courses inside THIS grouping's radius (and connected) --
        // the same set Stay & Play packages draw from.
        if (gid) {
          const cr = await tournamentApi.get(`/portal/reseller/groupings/${gid}/courses-in-radius`)
          setCourses(cr.data?.courses || [])
        }
      } catch (err) {
        setError(err?.response?.data?.message || err.message)
      } finally { setLoading(false) }
    })()
  }, [id])

  function toggleDay(i) {
    const arr = f.days.split('')
    arr[i] = arr[i] === '1' ? '0' : '1'
    set('days', arr.join(''))
  }

  const isCourseSelected = (cid) => selectedCourses.some(c => c.coursesId === cid)
  function toggleCourse(cid) {
    setSelectedCourses(prev => prev.some(c => c.coursesId === cid)
      ? prev.filter(c => c.coursesId !== cid)
      : [...prev, { coursesId: cid, minRounds: 0 }])
  }
  function setCourseMin(cid, val) {
    const n = Math.max(0, Math.floor(Number(val) || 0))
    setSelectedCourses(prev => prev.map(c => c.coursesId === cid ? { ...c, minRounds: n } : c))
  }
  // Rounds are a shared pool; per-course minimums can't reserve more than
  // the bundle holds. Only meaningful (and only shown) with 2+ courses.
  const multiCourse = selectedCourses.length > 1
  const totalMin = selectedCourses.reduce((s, c) => s + (Number(c.minRounds) || 0), 0)

  async function save() {
    setError(null)
    if (!groupingId) return setError(t('sp_bundles_err_grouping', 'Pick a destination.'))
    if (!f.name.trim()) return setError(t('sp_bundles_err_name', 'Name is required.'))
    if (selectedCourses.length === 0) return setError(t('sp_bundles_err_courses', 'Pick at least one course.'))
    if (!(Number(f.roundsCount) >= 1)) return setError(t('sp_bundles_err_rounds', 'Rounds must be at least 1.'))
    if (!(Number(f.price) >= 0)) return setError(t('sp_bundles_err_price', 'Enter a price.'))
    if (multiCourse && totalMin > Number(f.roundsCount)) {
      return setError(t('sp_bundles_err_min_total', 'Per-course minimums ({used}) can’t exceed the bundle’s total rounds ({total}).', { used: totalMin, total: f.roundsCount }))
    }

    // Single-course bundles carry no minimum (the whole pool plays there).
    const coursesPayload = selectedCourses.map((c, i) => ({
      coursesId: c.coursesId,
      minRounds: multiCourse ? (Number(c.minRounds) || 0) : 0,
      sortOrder: i,
    }))

    setSaving(true)
    try {
      const body = {
        groupingId: Number(groupingId),
        name: f.name.trim(), description: f.description || null, descriptionFr: f.descriptionFr || null,
        roundsCount: Number(f.roundsCount), price: Number(f.price),
        timeWindowStart: f.timeWindowStart || null, timeWindowEnd: f.timeWindowEnd || null,
        daysAllowed: f.days, expiryDate: f.expiryDate || null,
      }
      if (isNew) {
        body.courses = coursesPayload
        const { data } = await tournamentApi.post('/portal/reseller/bundles', body)
        navigate(`/portal/reseller/golf-bundles/${data.bundleId}`)
      } else {
        body.status = f.status
        await tournamentApi.put(`/portal/reseller/bundles/${id}`, body)
        await tournamentApi.put(`/portal/reseller/bundles/${id}/courses`, { courses: coursesPayload })
        setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2000)
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message)
    } finally { setSaving(false) }
  }

  if (loading) return <ResellerShell title={t('sp_bundles_editor_title', 'Bundle')}><div style={{ color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></ResellerShell>

  const pricingLocked = canEditPricing === false
  const backTo = groupingId ? `/portal/reseller/groupings/${groupingId}?tab=bundles` : '/portal/reseller/golf-bundles'

  return (
    <ResellerShell title={isNew ? t('sp_bundles_new_title', 'New bundle') : (f.name || t('sp_bundles_editor_title', 'Bundle'))}>
      <Link to={backTo} style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>
        {t('sp_bundles_back_to_group', '← Back to bundle group')}
      </Link>

      {error && <div style={{ ...errorBox, marginTop: 12 }}>{error}</div>}
      {savedFlash && <div style={{ ...successBox, marginTop: 12 }}>{t('sp_saved', 'Saved')}</div>}

      <div style={{ maxWidth: 640, marginTop: 16 }}>
        <SectionHeader>{t('sp_bundles_section_details', 'Details')}</SectionHeader>
        <Field label={t('sp_name_label', 'Name')}>
          <input style={inp} value={f.name} onChange={e => set('name', e.target.value)} placeholder={t('sp_bundles_name_ph', 'e.g. 4-Round Pass')} />
        </Field>
        <Field label={t('sp_description', 'Description')} hint={t('sp_bundles_desc_hint', 'Shown on the customer storefront. The embed shows whichever language the golfer is browsing in.')}>
          <BilingualMarkdownEditor
            valueEn={f.description} valueFr={f.descriptionFr}
            onChangeEn={v => set('description', v)} onChangeFr={v => set('descriptionFr', v)}
            placeholderEn={t('sp_bundles_desc_en_ph', 'e.g. Four prepaid rounds you can play any weekday this season.')}
            placeholderFr={t('sp_bundles_desc_fr_ph', 'p. ex. Quatre parties prépayées à jouer en semaine cette saison.')}
          />
        </Field>
        <Field
          label={t('sp_bundles_courses', 'Courses')}
          hint={t('sp_bundles_courses_hint', 'Pick one or more connected courses in this group. Rounds are a shared pool the golfer can use at any selected course. Set a per-course minimum only if a course needs a guaranteed number of rounds.')}
        >
          {courses.length === 0 ? (
            <div style={{ fontSize: 13, color: '#92400e', padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6 }}>
              {t('sp_bundles_no_courses', 'No courses in this group yet — set the radius on Settings and connect a course in range.')}{' '}
              {groupingId && <Link to={`/portal/reseller/groupings/${groupingId}?tab=settings`}>{t('settings', 'Settings')}</Link>}
            </div>
          ) : (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              {courses.map((c, idx) => {
                const sel = selectedCourses.find(s => s.coursesId === c.Courses_ID)
                const on = !!sel
                return (
                  <div key={c.Courses_ID} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                    borderTop: idx === 0 ? 'none' : '1px solid #f1f5f9',
                    background: on ? '#f0fdfa' : '#fff',
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, cursor: 'pointer', margin: 0 }}>
                      <input type="checkbox" checked={on} onChange={() => toggleCourse(c.Courses_ID)} style={{ width: 16, height: 16 }} />
                      <span style={{ fontSize: 14, color: '#0f172a' }}>
                        {c.CourseName}{c.City ? <span style={{ color: '#94a3b8' }}> — {c.City}</span> : null}
                      </span>
                    </label>
                    {on && multiCourse && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{t('sp_bundles_min_rounds', 'Min rounds')}</span>
                        <input
                          type="number" min={0} max={f.roundsCount}
                          value={sel.minRounds}
                          onChange={e => setCourseMin(c.Courses_ID, e.target.value)}
                          style={{ ...inp, width: 64, padding: '6px 8px' }}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {multiCourse && (
            <div style={{ fontSize: 12, marginTop: 8, color: totalMin > Number(f.roundsCount) ? '#b91c1c' : '#64748b' }}>
              {t('sp_bundles_min_total', '{used} of {total} rounds reserved as course minimums (the rest are free to use at any selected course).', { used: totalMin, total: f.roundsCount })}
            </div>
          )}
        </Field>

        <SectionHeader>{t('sp_pricing', 'Pricing')}</SectionHeader>
        {pricingLocked && (
          <div style={{ marginBottom: 12, padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
            {t('sp_pricing_locked_notice', 'Pricing is set by your partner account and can’t be changed here. You can still edit the package name, description, photos, included courses, availability, and play rules.')}
          </div>
        )}
        <div style={{ display: 'flex', gap: 16 }}>
          <Field label={t('sp_bundles_col_rounds', 'Rounds')} style={{ flex: 1 }}>
            <input type="number" min={1} disabled={pricingLocked} style={inp} value={f.roundsCount} onChange={e => set('roundsCount', e.target.value)} />
          </Field>
          <Field label={t('sp_bundles_price_label', 'Bundle price (total prepaid)')} style={{ flex: 1 }}>
            <input type="number" step="0.01" min={0} disabled={pricingLocked} style={inp} value={f.price} onChange={e => set('price', e.target.value)} placeholder="325.00" />
          </Field>
        </div>

        <SectionHeader>{t('sp_bundles_section_restrictions', 'Booking restrictions')}</SectionHeader>
        <Field label={t('sp_bundles_time_window', 'Time-of-day window')} hint={t('sp_bundles_time_window_hint', 'Leave blank for any time. Rounds can only be booked inside this window.')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="time" style={{ ...inp, width: 140 }} value={f.timeWindowStart} onChange={e => set('timeWindowStart', e.target.value)} />
            <span style={{ color: '#64748b' }}>{t('sp_bundles_to', 'to')}</span>
            <input type="time" style={{ ...inp, width: 140 }} value={f.timeWindowEnd} onChange={e => set('timeWindowEnd', e.target.value)} />
          </div>
        </Field>
        <Field label={t('sp_bundles_days', 'Days allowed')}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DAY_KEYS.map((d, i) => {
              const on = f.days[i] === '1'
              return (
                <button key={d} type="button" onClick={() => toggleDay(i)}
                  style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid ' + (on ? '#0f172a' : '#e2e8f0'), background: on ? '#0f172a' : '#fff', color: on ? '#fff' : '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {t('sp_day_' + d, d[0].toUpperCase() + d.slice(1))}
                </button>
              )
            })}
          </div>
        </Field>
        <Field label={t('sp_bundles_expiry', 'Expires')} hint={t('sp_bundles_expiry_hint', 'Unused rounds expire after this date (default: end of season). Editable later.')}>
          <input type="date" style={{ ...inp, width: 200 }} value={f.expiryDate} onChange={e => set('expiryDate', e.target.value)} />
        </Field>

        {!isNew && (
          <Field label={t('status', 'Status')}>
            <select style={{ ...inp, width: 200 }} value={f.status} onChange={e => set('status', e.target.value)}>
              <option value="draft">{t('sp_status_draft', 'draft')}</option>
              <option value="active">{t('sp_status_active', 'active')}</option>
              <option value="paused">{t('sp_status_paused', 'paused')}</option>
            </select>
          </Field>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
          <button onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
            {saving ? t('sp_saving', 'Saving…') : (isNew ? t('sp_bundles_create', 'Create bundle') : t('save', 'Save'))}
          </button>
          <Link to={backTo} style={secondaryBtn}>{t('cancel', 'Cancel')}</Link>
        </div>
      </div>
    </ResellerShell>
  )
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 16, ...style }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}
function SectionHeader({ children }) {
  return <h3 style={{ margin: '24px 0 12px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</h3>
}

const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const primaryBtn = { padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '10px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6 }
const successBox = { padding: 12, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 6 }
